/* eslint-disable ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes */
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { reads } from '@ember/object/computed';
import { capitalize } from '@ember/string';
import { htmlSafe } from '@ember/template';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import { isColour } from '@linc-technologies/linced-common/utils/colour';
import { tagName } from '@ember-decorators/component';
import {
  cellStatus,
  defaultIsInterim,
  flowPairs,
  isOnTrack,
  milestoneOptions,
  movementFor,
  percentOf,
  rulesVerb,
  thresholdFor,
} from 'linced-app/utils/progress-overview';

// Every band is the same height, so the chart reads as a scale rather than a
// histogram; the lines carry the size of each movement instead.
const ROW_HEIGHT = 76;
const ROW_GAP = 12;
// The widest band's lines fill all but this much of its height.
const BAND_PADDING = 12;
const MIN_LINE_WIDTH = 1.5;

// Where row `index` sits, shared by the level names and both columns of bands
// so they line up.
function rowStyle(index) {
  return htmlSafe(
    `top: ${index * (ROW_HEIGHT + ROW_GAP)}px; height: ${ROW_HEIGHT}px;`,
  );
}

/**
 * Insights::CohortFlow shows how each learner's descriptor changed between two
 * milestones: a column of descriptor bands for each, joined by one line per
 * movement. A line's thickness is the number of learners who made it, and it
 * takes the colour of the descriptor they started from.
 *
 * Selecting a band or a line hands the selection to the page, which lists
 * those learners in its drilldown panel.
 *
 * @param {Array<Object>} entries - the progress overview's resolved learner
 *   judgements ({id, fullName, yearLevel, byYear}).
 * @param {Array<{key, label, colour}>} levels - the progress levels,
 *   highest first, as the progress overview controller builds them.
 * @param {Array<string>} displayYears - academic year labels, oldest first.
 * @param {{mid: string, end: string}} labels - what the two slots of a year
 *   are called in the chart's mode.
 * @param {{interim: number, full: number}} onTrackGrades
 * @param {Object|null} active - the selection currently being looked at.
 */
@tagName('')
export default class CohortFlow extends Component {
  /** @type {Array<Object>|null} */
  entries = null;

  /** @type {Ember.Array|null} */
  levels = null;

  /** @type {Array<string>|null} */
  displayYears = null;

  /** @type {{mid: string, end: string}|null} */
  labels = null;

  /** @type {string|null} The page's chosen year. */
  selectedYear = null;

  /** @type {{interim: number, full: number}|null} */
  onTrackGrades = null;

  /** @type {Object|null} */
  active = null;

  /** @type {Function} Called with the selection when a band or line is picked. */
  onSelect() {}

  /** @type {Function} Called when the selection no longer applies. */
  onClear() {}

  /** @type {string|null} */
  @tracked fromKey = null;

  /** @type {string|null} */
  @tracked toKey = null;

  /** @type {Object|null} The line under the pointer, darkened on hover. */
  @tracked hoveredFlow = null;

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    // Compare the selected year's two slots, starting over whenever the page's
    // year changes. Between changes, From and To are free to be moved.
    let keys = this.milestones.map((milestone) => milestone.key);
    let year = this.selectedYear;
    let yearKeys = [`${year}:mid`, `${year}:end`];
    let lost = !keys.includes(this.fromKey) || !keys.includes(this.toKey);
    if (year !== this._seenYear || lost) {
      this._seenYear = year;
      let [fromKey, toKey] = yearKeys.every((key) => keys.includes(key))
        ? yearKeys
        : keys.slice(-2);
      this.fromKey = fromKey || null;
      this.toKey = toKey || null;
    }
  }

  @computed('displayYears.[]', 'labels')
  get milestones() {
    return milestoneOptions(this.displayYears, this.labels || {});
  }

  @computed('milestones.[]', 'fromKey')
  get fromMilestone() {
    return findBy(this.milestones, 'key', this.fromKey);
  }

  // A movement runs forwards in time, so From stops short of the last
  // milestone and To only offers milestones after From.
  @computed('milestones.[]')
  get fromOptions() {
    return this.milestones.slice(0, -1);
  }

  @computed('milestones.[]', 'fromKey')
  get toOptions() {
    let fromIndex = this.milestones.findIndex(
      (milestone) => milestone.key === this.fromKey,
    );
    return this.milestones.slice(fromIndex + 1);
  }

  @computed('milestones.[]', 'toKey')
  get toMilestone() {
    return findBy(this.milestones, 'key', this.toKey);
  }

  // Highest descriptor at the top, the way the tables read.
  @computed('levels.[]')
  get rows() {
    return (this.levels || []).map((level) => ({
      value: level.key,
      label: level.label,
      colour: isColour(level.colour) ? level.colour : '#26a69a',
    }));
  }

  @computed('entries.[]', 'fromMilestone', 'toMilestone')
  get pairs() {
    if (isEmpty(this.fromMilestone) || isEmpty(this.toMilestone)) {
      return [];
    }
    return flowPairs(this.entries, this.fromMilestone, this.toMilestone);
  }

  @reads('pairs.length')
  total;

  @computed('rows.length')
  get height() {
    let count = this.rows.length;
    return count ? count * ROW_HEIGHT + (count - 1) * ROW_GAP : 0;
  }

  // The part of the selection that applies to the milestones being shown.
  @computed('active', 'fromKey', 'toKey')
  get _active() {
    let active = this.active;
    if (
      isEmpty(active) ||
      active.fromKey !== this.fromKey ||
      active.toKey !== this.toKey
    ) {
      return null;
    }
    return active;
  }

  /**
   * layout is everything about the chart that depends on the data: the lines,
   * their thickness and stacking, and each side's bands and threshold lines.
   * It is kept apart from the selection, so picking a band or a line only
   * re-applies the highlight below rather than laying the chart out again.
   */
  @computed(
    'fromMilestone',
    'height',
    'onTrackGrades.{full,interim}',
    'pairs.[]',
    'rows.[]',
    'toMilestone',
    'total',
  )
  get layout() {
    let rows = this.rows;
    let pairs = this.pairs;
    let total = this.total;
    let grades = this.onTrackGrades;
    let indexOf = (value) => rows.findIndex((row) => row.value === value);
    let centre = (index) => index * (ROW_HEIGHT + ROW_GAP) + ROW_HEIGHT / 2;
    let share = (count) => percentOf(count, total);
    let labelFor = (value) => rows[indexOf(value)].label;

    // One flow per (from, to) pair that anyone made.
    let flows = [];
    pairs.forEach((pair) => {
      let flow = flows.find((f) => f.from === pair.from && f.to === pair.to);
      if (!flow) {
        flow = { from: pair.from, to: pair.to, count: 0 };
        flows.push(flow);
      }
      flow.count++;
    });

    let countsFor = (key) =>
      rows.map((row) => pairs.filter((pair) => pair[key] === row.value).length);
    // One scale for every line, so equal thickness always means an equal
    // number of learners.
    let widest = Math.max(0, ...countsFor('from'), ...countsFor('to'));
    let scale = widest ? (ROW_HEIGHT - BAND_PADDING) / widest : 0;
    let widthOf = (count) => Math.max(MIN_LINE_WIDTH, count * scale);

    // Stack each band's lines in the order they leave or arrive, so they
    // cross as little as possible.
    let stack = (side, other) => {
      rows.forEach((row, index) => {
        let own = flows
          .filter((flow) => flow[side] === row.value)
          .sort((a, b) => indexOf(a[other]) - indexOf(b[other]));
        let height = own.reduce((sum, flow) => sum + widthOf(flow.count), 0);
        let y = centre(index) - height / 2;
        own.forEach((flow) => {
          let width = widthOf(flow.count);
          flow[`${side}Y`] = y + width / 2;
          y += width;
        });
      });
    };
    stack('from', 'to');
    stack('to', 'from');

    let lines = flows
      .sort((a, b) => a.count - b.count)
      .map((flow) => {
        let width = widthOf(flow.count);
        return {
          from: flow.from,
          to: flow.to,
          count: flow.count,
          path: `M0 ${flow.fromY} C50 ${flow.fromY} 50 ${flow.toY} 100 ${flow.toY}`,
          fromY: flow.fromY,
          toY: flow.toY,
          colour: rows[indexOf(flow.from)].colour,
          width,
          // Thin lines are hard to hit, so each gets a wider invisible
          // target.
          hitWidth: Math.max(width, 10),
          title: `${labelFor(flow.from)} to ${labelFor(flow.to)}: ${
            flow.count
          } ${flow.count === 1 ? 'student' : 'students'} (${share(
            flow.count,
          )}%), ${movementFor(flow.from, flow.to)}`,
        };
      });

    let side = (milestone, key) => {
      let slots = pairs.map((pair) => pair[`${key}Slot`]);
      // Which rules this side's judgements were made under. A half year can
      // hold both; an interim or full slot only ever holds its own.
      let rules = {
        interim: slots.some((slot) => slot.isInterim),
        full: slots.some((slot) => !slot.isInterim),
      };
      if (!rules.interim && !rules.full) {
        rules[defaultIsInterim(milestone.half) ? 'interim' : 'full'] = true;
      }
      let verb = rulesVerb(rules, milestone.half);

      let bands = rows.map((row, index) => {
        let own = slots.filter((slot) => slot.judgement === row.value);
        let onTrack = own.filter((slot) => isOnTrack(slot, grades)).length;
        // Read the way the count tables read their cells; an empty band
        // reads as its level would under the side's rule.
        let status = {
          on: capitalize(verb),
          off: `Not ${verb}`,
          mixed: `${onTrack} of ${own.length} ${verb}`,
        }[
          cellStatus(
            own.length,
            onTrack,
            row.value,
            milestone.half,
            grades,
            !!rules.interim,
          )
        ];
        return {
          value: row.value,
          label: row.label,
          count: own.length,
          pct: share(own.length),
          status,
          style: rowStyle(index),
          tintStyle: htmlSafe(`background-color: ${row.colour};`),
          accentStyle: htmlSafe(`background-color: ${row.colour};`),
        };
      });

      // One line per rule in play, in the gap below the lowest band that
      // passes it. Where both rules fall in the same gap, they share a line.
      let thresholds = [];
      [
        { isInterim: true, label: 'On track line' },
        { isInterim: false, label: 'Met line' },
      ]
        .filter((rule) => rules[rule.isInterim ? 'interim' : 'full'])
        .forEach((rule) => {
          let threshold = thresholdFor(rule.isInterim, grades);
          let lastPassing = rows.reduce(
            (last, row, index) => (row.value >= threshold ? index : last),
            -1,
          );
          let top =
            lastPassing < 0
              ? -ROW_GAP / 2
              : (lastPassing + 1) * (ROW_HEIGHT + ROW_GAP) - ROW_GAP / 2;
          let shared = thresholds.find((line) => line.top === top);
          if (shared) {
            shared.label = 'On track and met line';
            shared.isInterim = false;
            return;
          }
          thresholds.push({
            top,
            label: rule.label,
            isInterim: rule.isInterim,
          });
        });

      return {
        milestone,
        mixed: !!(rules.interim && rules.full),
        bands,
        lines: thresholds.map((line) => ({
          label: line.label,
          // With both lines on one side, the interim one is dashed so the
          // two can be told apart.
          dashed: line.isInterim && thresholds.length > 1,
          lineStyle: htmlSafe(`top: ${line.top - 1}px;`),
          labelStyle: htmlSafe(`top: ${line.top}px;`),
        })),
      };
    };

    return {
      from: side(this.fromMilestone, 'from'),
      to: side(this.toMilestone, 'to'),
      flows: lines,
      viewBox: `0 0 100 ${this.height}`,
      columnStyle: htmlSafe(`height: ${this.height}px;`),
    };
  }

  // chart is the layout with the current selection highlighted: the lines it
  // covers stand out, and the bands they do not touch fade. The line under
  // the pointer darkens, as the bars do on hover.
  @computed('_active', 'hoveredFlow', 'layout')
  get chart() {
    let layout = this.layout;
    let active = this._active;
    let hovered = this.hoveredFlow;
    let isHovered = (flow) =>
      !!hovered && flow.from === hovered.from && flow.to === hovered.to;
    if (!active) {
      return Object.assign({}, layout, {
        flows: layout.flows.map((flow) =>
          Object.assign({}, flow, {
            isActive: false,
            isHovered: isHovered(flow),
            opacity: 0.65,
          }),
        ),
      });
    }

    let flows = layout.flows.map((flow) => {
      let isActive =
        (isEmpty(active.fromJudgement) || flow.from === active.fromJudgement) &&
        (isEmpty(active.toJudgement) || flow.to === active.toJudgement);
      return Object.assign({}, flow, {
        isActive,
        isHovered: isHovered(flow),
        opacity: isActive ? 0.9 : 0.08,
      });
    });
    let activeFlows = flows.filter((flow) => flow.isActive);
    let dim = (side, key) =>
      Object.assign({}, side, {
        bands: side.bands.map((band) =>
          Object.assign({}, band, {
            isDimmed: !activeFlows.some((flow) => flow[key] === band.value),
          }),
        ),
      });
    return Object.assign({}, layout, {
      flows,
      from: dim(layout.from, 'from'),
      to: dim(layout.to, 'to'),
    });
  }

  // The labels and bands share a row layout, so the names line up with them.
  @computed('rows.[]')
  get labelRows() {
    return this.rows.map((row, index) => ({
      label: row.label,
      style: rowStyle(index),
    }));
  }

  _select(fromJudgement, toJudgement) {
    this.onSelect({
      fromKey: this.fromKey,
      toKey: this.toKey,
      fromMilestone: this.fromMilestone,
      toMilestone: this.toMilestone,
      fromJudgement,
      toJudgement,
    });
  }

  @action
  clear() {
    this.onClear();
  }

  @action
  setFrom(key) {
    let keys = this.milestones.map((milestone) => milestone.key);
    let fromIndex = keys.indexOf(key);
    // Keep To after From, moving it to the next milestone if needed.
    let toKey =
      keys.indexOf(this.toKey) > fromIndex ? this.toKey : keys[fromIndex + 1];
    this.fromKey = key;
    this.toKey = toKey;
    this.onClear();
  }

  @action
  setTo(key) {
    this.toKey = key;
    this.onClear();
  }

  @action
  selectFromBand(band) {
    if (band.count) {
      this._select(band.value, null);
    }
  }

  @action
  selectToBand(band) {
    if (band.count) {
      this._select(null, band.value);
    }
  }

  @action
  selectFlow(flow) {
    this._select(flow.from, flow.to);
  }

  @action
  hoverFlow(flow) {
    this.hoveredFlow = { from: flow.from, to: flow.to };
  }

  @action
  unhoverFlow() {
    this.hoveredFlow = null;
  }
}
